/**
 * feat-243 — the conflict banner. One refused write, explained, with the one
 * action that can be honestly offered for it.
 *
 * ── IT IS A BANNER, IN THE TAB-LEVEL SLOT, NOT A DIALOG ───────────────────
 * The whole point of the 409 flow is that the author reads the FRESH list. A
 * modal covers the exact thing the message is telling them to look at, and its
 * dismiss is a decision they have to make before they are allowed to see the
 * evidence for it. So this renders in the slot `writeError` occupies, above the
 * view bar and the grid, where it and the list are on screen together — and
 * where it serves membership conflicts (centre + library panes) and prereq
 * conflicts (Settings subtab) from one place. `design/CONFLICT-BRIEF.md` §1.
 *
 * ── EVERY CLASS IN THE FAMILY COMES FROM `attentionClasses.ts` ────────────
 * `attention-*` means "an author-fixable problem in authored content", which is
 * exactly what a 409 is: nothing broke, the guard did its job, and the author is
 * the only person who can decide what happens next. It is deliberately NOT
 * `danger-*` — red is reserved for an engineering fault the author cannot fix
 * (`DefaultTrackAlert`), and painting a working guard red teaches the author the
 * editor is flaky while making a real 500 indistinguishable from a normal
 * Tuesday. The census in `curriculumMap.census.feat242.test.tsx` enforces that
 * no file outside `attentionClasses.ts` writes one of these literals.
 *
 * ── NO HUE-ONLY SIGNAL ────────────────────────────────────────────────────
 * `+` / `−` / `⇅` carry the diff and the copy carries the state.
 * `attention-surface` and `danger-surface` are L 0.908 vs 0.910 — a design
 * leaning on fill collapses in greyscale and for a red-deficient reader.
 */

import React, { useEffect, useRef } from 'react';
import {
  ATTENTION_BORDER,
  ATTENTION_BUTTON,
  ATTENTION_INK,
  ATTENTION_ON_SURFACE_CHIP,
  ATTENTION_SURFACE,
} from './attentionClasses';
import { CHIP_CAP, CONFLICT_COPY, CONFLICT_LABELS, DIFF_GLYPH } from './conflictCopy';
import type { DiffKind } from './conflictCopy';
import type { ReplayIntent, TrackConflict } from './trackConflict';

export interface TrackConflictBannerProps {
  conflict: TrackConflict;
  intent: ReplayIntent;
  /**
   * Resolve one id to its display title, or **null** when it resolves to
   * nothing. Null is reachable the moment someone is also editing the Lessons
   * tab, and it renders the raw id in `font-mono` — never a blank chip, never
   * the string `undefined`.
   */
  titleOf: (id: string) => string | null;
  /** Any write is in flight — the button cannot fire a second one. */
  busy: boolean;
  /** THIS banner's replay is the write in flight (S9). Separate from `busy`
   *  because a drag elsewhere must not relabel the button "Re-applying…". */
  reapplying: boolean;
  onReapply: () => void;
  onDismiss: () => void;
}

/** One id, as it appears in a chip: a title, or the raw id flagged as raw. */
function chipFor(id: string, titleOf: (id: string) => string | null) {
  const title = titleOf(id);
  return title ? { text: title, raw: false } : { text: id, raw: true };
}

const Chip: React.FC<{ id: string; kind: DiffKind; titleOf: (id: string) => string | null }> = ({
  id,
  kind,
  titleOf,
}) => {
  const { text, raw } = chipFor(id, titleOf);
  const label = `${DIFF_GLYPH[kind]} ${text}`;
  return (
    <span
      title={label}
      data-testid={`conflict-chip-${id}`}
      data-kind={kind}
      className={`text-[11px] font-bold ${ATTENTION_ON_SURFACE_CHIP} rounded-full px-2 py-0.5 max-w-[220px] truncate${
        raw ? ' font-mono' : ''
      }`}
    >
      {label}
    </span>
  );
};

/** Up to `CHIP_CAP` chips, then a plain count. The count carries no chip border
 *  — it is a count, not a thing — and it exists so a 31-lesson tenant cannot
 *  push the pane's height around. */
const ChipGroup: React.FC<{
  ids: string[];
  kind: DiffKind;
  titleOf: (id: string) => string | null;
}> = ({ ids, kind, titleOf }) => (
  <>
    {ids.slice(0, CHIP_CAP).map((id) => (
      <Chip key={`${kind}-${id}`} id={id} kind={kind} titleOf={titleOf} />
    ))}
    {ids.length > CHIP_CAP && (
      <span className="text-[11px] font-bold px-1">
        {CONFLICT_COPY.moreCount(ids.length - CHIP_CAP)}
      </span>
    )}
  </>
);

export const TrackConflictBanner: React.FC<TrackConflictBannerProps> = ({
  conflict,
  intent,
  titleOf,
  busy,
  reapplying,
  onReapply,
  onDismiss,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // The author's pointer was in the centre pane or the library pane; the
  // explanation is at the top of the tab. Without this a keyboard author tabs
  // forward into a list that silently changed under them.
  useEffect(() => {
    ref.current?.focus();
  }, [conflict]);

  const anonymous = conflict.changedBy === null;
  const groups: Record<DiffKind, string[]> = {
    added: conflict.added,
    removed: conflict.removed,
    moved: conflict.moved,
  };
  const n = conflict.added.length + conflict.removed.length + conflict.moved.length;

  const titlesOf = (ids: string[]) => ids.map((id) => chipFor(id, titleOf).text);

  const headline = CONFLICT_COPY.headline({
    who: conflict.changedBy,
    scope: conflict.scope,
    added: titlesOf(conflict.added),
    removed: titlesOf(conflict.removed),
    moved: titlesOf(conflict.moved),
  });

  const subline = CONFLICT_COPY.subline({
    scope: conflict.scope,
    isReorder: intent.isPureReorder,
  });

  const kinds = (['added', 'removed', 'moved'] as DiffKind[]).filter((k) => groups[k].length > 0);

  return (
    <div
      ref={ref}
      role="alert"
      tabIndex={-1}
      data-testid="track-conflict-banner"
      data-scope={conflict.scope}
      data-suppressed={intent.suppressed ?? 'none'}
      // No `outline-none`: this container is focused programmatically, and a
      // keyboard author who has just been moved needs to see where they landed.
      // Suppressing the ring would make the focus move invisible, which is the
      // opposite of what `tabIndex={-1}` is here for.
      className={`mx-5 mt-4 shrink-0 rounded-xl border ${ATTENTION_BORDER} ${ATTENTION_SURFACE} ${ATTENTION_INK} px-3.5 py-2.5 text-[12.5px]`}
    >
      <div className="flex items-start gap-2.5">
        <span aria-hidden className="text-base leading-none mt-0.5">
          {CONFLICT_LABELS.bannerGlyph}
        </span>
        <div className="min-w-0 flex-1">
          {/* A long shared-mailbox address WRAPS rather than truncating — a
              truncated address is one you cannot act on.

              SECURITY HIGH-1 — `data-sentry-mask`, same mechanism and same reason
              as feat-241's crew roster (`CoverageMatrix.tsx`,
              `CurriculumPassportPage.tsx`). `instrument.ts` sets
              `maskAllText: false` with session replay at 10% of sessions and 100%
              of error sessions, so without this marker the actor's raw email is
              recorded as plain replay text — and `App.tsx`'s
              `Sentry.setUser({ email })` stores it correlated with the identity
              of the manager who saw the conflict.

              MASKED PER-ELEMENT, NOT ON THE BANNER. Masking walks ancestors, so a
              marker on the container would swallow the sub-line, the chips and
              the re-apply sentence — the entire explanation of what happened,
              which carries no personal data and is the only thing that makes a
              replay of this surface worth having.

              The email appears in NO attribute anywhere in this subtree, and that
              is load-bearing rather than incidental: `maskAttribute()` returns
              early when `maskAllText` is false, so an attribute copy of this
              string would be masked nowhere and the marker below would be a
              placebo. Do not add `title={headline}` to make the wrap tidier. */}
          <p data-sentry-mask className="font-bold break-words" data-testid="conflict-headline">
            {headline}
          </p>
          <p className="mt-1 text-[11.5px]" data-testid="conflict-subline">
            {/* Secondary by SIZE, never by an opacity modifier on the ink —
                `attentionClasses.ts` rule 1. */}
            {anonymous ? `${subline} ${CONFLICT_COPY.anonymousNote()}` : subline}
          </p>

          {n > 0 &&
            (n <= 3 ? (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="conflict-chips">
                <span className="text-[11px] font-bold">{CONFLICT_COPY.chipRowLabel(anonymous)}</span>
                {kinds.map((kind) => (
                  <ChipGroup key={kind} ids={groups[kind]} kind={kind} titleOf={titleOf} />
                ))}
              </div>
            ) : (
              <div className="mt-2 space-y-1" data-testid="conflict-chips">
                {kinds.map((kind) => (
                  <div key={kind} className="flex flex-wrap items-center gap-1.5" data-kind={kind}>
                    <span className="text-[11px] font-bold w-[74px] shrink-0">
                      {CONFLICT_COPY.groupLabel(kind, groups[kind].length)}
                    </span>
                    <ChipGroup ids={groups[kind]} kind={kind} titleOf={titleOf} />
                  </div>
                ))}
              </div>
            ))}

          {/* THE ACTION ROW. `flex-wrap`, and the button and the sentence
              describing it are NOT coupled — below roughly 1000px of banner
              width the button drops onto its own line and the sentence shares
              the line beneath it. That is the intended degradation, so no
              `whitespace-nowrap` on the pair and the sentence does not move
              inside the button's flex child. */}
          <div className="mt-2.5 flex flex-wrap items-center gap-3">
            {intent.suppressed ? (
              <>
                {/* The absence of a control is invisible; a sentence in the
                    control's slot is a decision. Same geometry and radius as the
                    button, so the slot reads as occupied rather than empty. */}
                <span
                  data-testid="conflict-no-reapply"
                  className={`font-display font-semibold rounded-full px-4 py-1.5 text-[12px] ${ATTENTION_ON_SURFACE_CHIP} shrink-0`}
                >
                  {intent.suppressed === 'reorder'
                    ? CONFLICT_LABELS.reorderPill
                    : CONFLICT_LABELS.noopPill}
                </span>
                <span className="text-[11.5px] min-w-0 break-words">
                  {intent.suppressed === 'reorder'
                    ? CONFLICT_COPY.reorderReason()
                    : CONFLICT_COPY.noopReason({
                        scope: conflict.scope,
                        adds: titlesOf(intent.myAdded),
                        removes: titlesOf(intent.myRemoved),
                      })}
                </span>
              </>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="conflict-reapply"
                  onClick={onReapply}
                  disabled={busy}
                  className={`font-display font-semibold rounded-full px-4 py-1.5 text-[12px] ${ATTENTION_BUTTON} hover:brightness-110 disabled:opacity-50 shrink-0`}
                >
                  {reapplying ? CONFLICT_LABELS.reapplying : CONFLICT_LABELS.reapply}
                </button>
                {/* Saying what the button will do BEFORE it is pressed is what
                    makes an explicit replay honest rather than a merge with a
                    button on it (R2). Do not drop this line to save a row. */}
                <span className="text-[11.5px] min-w-0 break-words" data-testid="conflict-reapply-line">
                  {CONFLICT_COPY.reapplyLine({
                    scope: conflict.scope,
                    adds: titlesOf(intent.myAdded),
                    removes: titlesOf(intent.myRemoved),
                  })}
                </span>
              </>
            )}
            {/* Dismiss is disabled while the replay is in flight — dismissing
                mid-flight orphans the outcome, and the banner staying up is the
                only stated reason the pane is about to change. */}
            <button
              type="button"
              data-testid="conflict-dismiss"
              onClick={onDismiss}
              disabled={reapplying}
              className="text-[11.5px] font-semibold hover:underline disabled:opacity-50 disabled:no-underline shrink-0 ml-auto"
            >
              {CONFLICT_LABELS.dismiss}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackConflictBanner;
